import { useEffect, useLayoutEffect, useRef } from 'react';

const PARAM_PREFIX_SEPARATOR = '_';

/**
 * Checks whether the current URL search string contains any query
 * parameters that match the given prefixes.  A prefix matches a key
 * when the key equals the prefix or starts with `<prefix>_`.
 */
function urlHasParams(prefixes: string[]): boolean {
  const keys = Array.from(new URLSearchParams(window.location.search).keys());
  return keys.some((key) =>
    prefixes.some(
      (p) => key === p || key.startsWith(p + PARAM_PREFIX_SEPARATOR),
    ),
  );
}

/**
 * Generic hook that persists a state object to `localStorage` and
 * restores it on mount when the URL contains no matching query params.
 *
 * Designed to work alongside nuqs: nuqs owns the URL state, and this
 * hook simply shadows it in localStorage so it survives navigation.
 *
 * @param storageKey   localStorage key to read/write.
 * @param state        Current state object (serialisable to JSON).
 * @param restore      Callback invoked on mount with the saved state
 *                     when the URL has no matching params.
 * @param paramPrefixes URL param prefixes used to detect whether the
 *                     URL already carries state (e.g. `['ts_', 'vs_']`).
 */
export function usePersistedState<T>(
  storageKey: string,
  state: T,
  restore: (saved: T) => void,
  paramPrefixes: string[],
): void {
  const didRestore = useRef<boolean>(false);
  const isFirstSave = useRef<boolean>(true);

  // --- Restore (before paint) ----------------------------------------
  useLayoutEffect(() => {
    if (didRestore.current) {
      return;
    }
    didRestore.current = true;

    // If the URL already contains relevant params, let nuqs handle it.

    if (urlHasParams(paramPrefixes)) {
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (raw != null) {
        const saved = JSON.parse(raw) as T;
        restore(saved);
      }
    } catch {
      // Corrupt / incompatible data — silently ignore.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Save on every config change -----------------------------------
  useEffect(() => {
    // Skip the first save so we don't overwrite localStorage with
    // defaults before the restore effect has had a chance to fire.
    if (isFirstSave.current) {
      isFirstSave.current = false;
      return;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Storage full or unavailable — silently ignore.
    }
  }, [storageKey, state]);
}
